import type { DynamicClient } from '../../client/types';
import { fetchAndStoreNonces } from './fetchAndStoreNonces/fetchAndStoreNonces';
import { updateNoncePoolStorage } from './noncePoolStorage/updateNoncePoolStorage/updateNoncePoolStorage';

/**
 * Returns a nonce for wallet ownership verification.
 *
 * The pool lives exclusively in storage: every call pops the first nonce
 * through `updateNoncePoolStorage`'s serialized read-modify-write, so the
 * nonce is removed from storage before it is handed out and no in-memory
 * copy can go stale. Nonces are single-use server-side — a stale copy handed
 * out twice fails the second consume.
 *
 * When the pool is running low (≤1 remaining after pop) a background refetch
 * is triggered. If the pool is empty or expired, nonces are fetched on-demand
 * before returning.
 * @notInstrumented
 */
export const getNonce = async (client: DynamicClient): Promise<string> => {
  let nonce: string | undefined;
  let remainingCount = 0;

  await updateNoncePoolStorage(
    {
      update: (stored) => {
        // An expired pool is discarded rather than served: the server has
        // already evicted its nonces.
        if (
          !stored ||
          stored.nonces.length === 0 ||
          stored.expiration <= Date.now()
        ) {
          return null;
        }

        const [first, ...remaining] = stored.nonces;

        nonce = first;
        remainingCount = remaining.length;

        return { expiration: stored.expiration, nonces: remaining };
      },
    },
    client
  );

  if (nonce === undefined) {
    await fetchAndStoreNonces(client);

    return getNonce(client);
  }

  if (remainingCount <= 1) {
    void fetchAndStoreNonces(client);
  }

  return nonce;
};
